import { Injectable, inject } from '@angular/core';
import { UserService } from './user.service';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, catchError, debounceTime, filter, of, take } from 'rxjs';
import { LogLevel } from '../models/enums/log-level';

@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private userService = inject(UserService);
  private httpClient = inject(HttpClient);

  private consoleLog = console.log;
  private consoleDebug = console.debug;
  private consoleInfo = console.info;
  private consoleWarn = console.warn;
  private consoleError = console.error;

  private unauthPassword: string = environment.unauthPassword;

  private logSubject = new BehaviorSubject<Array<{ level: LogLevel; message: string }>>([]);

  constructor() {
    this.logSubject
      .pipe(
        debounceTime(1000),
        filter((logs) => logs.length > 0),
      )
      .subscribe((logs) => this.flushLogs(logs));
  }

  /**
   * Logs a debug message.
   * @param message - The main message to log.
   * @param optionalParams - Additional parameters to include.
   */
  public debug(message: any, ...optionalParams: any[]): void {
    this.log(LogLevel.Debug, message, ...optionalParams);
  }

  /**
   * Logs an informational message.
   * @param message - The main message to log.
   * @param optionalParams - Additional parameters to include.
   */
  public info(message: any, ...optionalParams: any[]): void {
    this.log(LogLevel.Info, message, ...optionalParams);
  }

  /**
   * Logs a warning message.
   * @param message - The main message to log.
   * @param optionalParams - Additional parameters to include.
   */
  public warn(message: any, ...optionalParams: any[]): void {
    this.log(LogLevel.Warn, message, ...optionalParams);
  }

  /**
   * Logs an error message.
   * @param error - The error object or message to log.
   * @param optionalParams - Additional parameters to include.
   */
  public error(error: any, ...optionalParams: any[]): void {
    if (typeof error === 'string') {
      this.log(LogLevel.Error, error, ...optionalParams);
    } else if (error instanceof Error) {
      this.log(LogLevel.Error, error.message, ...optionalParams, error);
    } else {
      let errorMessage: string;
      try {
        errorMessage = JSON.stringify(error, null, 2); // Pretty print JSON
      } catch (jsonError) {
        errorMessage = error.toString();
      }
      this.log(LogLevel.Error, errorMessage, ...optionalParams, error);
    }
  }

  /**
   * Logs a message with the specified log level.
   * @param level - The severity level of the log.
   * @param message - The main message to log.
   * @param optionalParams - Additional parameters to include.
   */
  private log(level: LogLevel, message: any, ...optionalParams: any[]): void {
    if (level < environment.logLevel) {
      return;
    }

    const formattedMessage = this.formatMessage(message);
    const fullMessage = [formattedMessage, ...optionalParams.map(this.formatMessage)].join(' ');
    this.logToConsole(level, fullMessage);
    if (environment.env !== 'REMOVEME') {
      const currentLogs = this.logSubject.getValue();
      this.logSubject.next([...currentLogs, { level, message: fullMessage }]);
    }
  }

  private formatMessage(message: any): string {
    if (typeof message === 'string') {
      return message;
    }
    try {
      return JSON.stringify(message, null, 2); // Pretty print JSON
    } catch {
      return String(message);
    }
  }

  /**
   * Logs a message to the console with color coding based on log level.
   * @param level - The severity level of the log.
   * @param message - The message to log.
   */
  private logToConsole(level: LogLevel, message: string): void {
    const colorMap = {
      [LogLevel.Debug]: '#008000',
      [LogLevel.Info]: '#6495ED',
      [LogLevel.Warn]: '#FF8C00',
      [LogLevel.Error]: '#DC143C',
    };
    const color = colorMap[level] || '#000000';

    switch (level) {
      case LogLevel.Debug:
        this.consoleDebug(`%c${message}`, `color: ${color}`);
        break;
      case LogLevel.Info:
        this.consoleInfo(`%c${message}`, `color: ${color}`);
        break;
      case LogLevel.Warn:
        this.consoleWarn(`%c${message}`, `color: ${color}`);
        break;
      case LogLevel.Error:
        this.consoleError(`%c${message}`, `color: ${color}`);
        break;
      default:
        this.consoleLog(message);
    }
  }

  private async flushLogs(logs: Array<{ level: LogLevel; message: string }>) {
    if (environment.env !== 'REMOVEME') {
      try {
        await this.sendLogsToCloudWatch(logs);
      } catch (error) {
        this.consoleError('Failed to send logs to CloudWatch:', error);
      }
    }
    // Clear the logs after sending
    this.logSubject.next([]);
  }

  /**
   * Sends log data to CloudWatch using HTTP POST request.
   * @param logData - The log data to send.
   */
  private async sendLogsToCloudWatch(logs: Array<{ level: LogLevel; message: string }>) {
    if (environment.cloudWatchLog) {
      const url = `${environment.loggingUrl}/front/${(await this.userService.isAuthenticated()) ? '' : 'unauth/'}logs`;
      const options = (await this.userService.isAuthenticated()) ? {} : { headers: { Authorization: this.unauthPassword } };
      this.httpClient
        .post(url, { logs }, options)
        .pipe(
          take(1),
          catchError((err) => {
            this.consoleError(err);
            this.consoleWarn('Disabling log collection');
            environment.cloudWatchLog = false;
            return of();
          }),
        )
        .subscribe();
    }
  }
}
